import React, { useState } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip,
  Alert, LinearProgress, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';


const DocumentsStep = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  isCompleted,
}) => {
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState(formData.documents || []);

  const documentTypes = [
    { value: 'id_proof', label: 'ID Proof (Aadhar/PAN/Passport)' },
    { value: 'mci_certificate', label: 'MCI Registration Certificate' },
    { value: 'rci_certificate', label: 'RCI Registration Certificate' },
    { value: 'degree_certificate', label: 'Degree Certificate' },
    { value: 'experience_certificate', label: 'Experience Certificate' },
    { value: 'clinic_registration', label: 'Clinic Registration Certificate' },
    { value: 'insurance_certificate', label: 'Professional Insurance Certificate' },
    { value: 'other', label: 'Other Documents' },
  ];

  const handleFileSelect = (event, docType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF and image files are allowed');
      return;
    }

    const newDocument = {
      id: Date.now().toString(),
      name: file.name,
      type: docType,
      size: file.size,
      status: 'uploading',
      progress: 0,
    };

    setDocuments(prev => [...prev, newDocument]);
    simulateFileUpload(newDocument, file);
  };

  const simulateFileUpload = async (document, file) => {
    setUploading(true);
    
    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === document.id 
            ? { ...doc, progress: i }
            : doc
        )
      );
    }

    // Simulate successful upload
    setDocuments(prev => 
      prev.map(doc => 
        doc.id === document.id 
          ? { ...doc, status: 'uploaded', progress: 100 }
          : doc
      )
    );

    setUploading(false);
  };

  const handleRemoveDocument = (documentId: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const handleSubmit = () => {
    const uploadedDocs = documents.filter(doc => doc.status === 'uploaded');
    if (uploadedDocs.length === 0) {
      alert('Please upload at least one document');
      return;
    }

    const docData = {
      documents: uploadedDocs.map(doc => ({
        doc_type: doc.type,
        file_id: doc.id,
        file_name: doc.name,
      })),
    };

    onSubmit(docData);
  };

  const getDocumentTypeLabel = (type: string) => {
    return documentTypes.find(dt => dt.value === type)?.label || type;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Document Verification
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Upload your professional documents for verification
      </Typography>

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Documents have been uploaded successfully!
        </Alert>
      )}

      {/* Document Upload Grid */}
      <Grid container spacing={3} mb={4}>
        {documentTypes.map((docType) => (
          <Grid item xs={12} sm={6} md={4} key={docType.value}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' }
              }}
            >
              <CardContent>
                <Box textAlign="center">
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {docType.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Upload {docType.label.toLowerCase()}
                  </Typography>
                  
                  <input
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    id={`file-upload-${docType.value}`}
                    type="file"
                    onChange={(e) => handleFileSelect(e, docType.value)}
                    disabled={isCompleted}
                  />
                  <label htmlFor={`file-upload-${docType.value}`}>
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={isCompleted}
                      fullWidth
                    >
                      Choose File
                    </Button>
                  </label>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Uploaded Documents List */}
      {documents.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Uploaded Documents
          </Typography>
          
          <List>
            {documents.map((doc) => (
              <ListItem key={doc.id} divider>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getDocumentTypeLabel(doc.type)} • {formatFileSize(doc.size)}
                      </Typography>
                      {doc.status === 'uploading' && doc.progress !== undefined && (
                        <Box mt={1}>
                          <LinearProgress 
                            variant="determinate" 
                            value={doc.progress} 
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {doc.progress}% uploaded
                          </Typography>
                        </Box>
                      )}
                      {doc.status === 'error' && (
                        <Typography variant="caption" color="error">
                          {doc.error}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" alignItems="center" gap={1}>
                    {doc.status === 'uploaded' && (
                      <CheckCircleIcon color="success" />
                    )}
                    {doc.status === 'error' && (
                      <ErrorIcon color="error" />
                    )}
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveDocument(doc.id)}
                      disabled={isCompleted}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Requirements */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Document Requirements:</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>Maximum file size: 10MB</li>
          <li>Supported formats: PDF, JPG, JPEG, PNG</li>
          <li>All documents must be clear and legible</li>
          <li>Documents will be reviewed within 2-3 business days</li>
        </Typography>
      </Alert>

      {!isCompleted && (
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || uploading || documents.filter(d => d.status === 'uploaded').length === 0}
          >
            {submitting || uploading ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DocumentsStep;
